import { useState, useRef, useEffect } from "react";
import { Code2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface MacroGroup {
  label: string;
  macros: { token: string; description: string }[];
}

const MACRO_GROUPS: MacroGroup[] = [
  {
    label: "Sub IDs",
    macros: [
      { token: "{sub1}", description: "Sub ID 1" },
      { token: "{sub2}", description: "Sub ID 2" },
      { token: "{sub3}", description: "Sub ID 3" },
      { token: "{sub4}", description: "Sub ID 4" },
      { token: "{sub5}", description: "Sub ID 5" },
    ],
  },
  {
    label: "Advertiser / Affiliate",
    macros: [
      { token: "{advertiser_id}", description: "Advertiser ID" },
      { token: "{advertiser_name}", description: "Advertiser name" },
      { token: "{affiliate_id}", description: "Affiliate ID" },
      { token: "{affiliate_name}", description: "Affiliate name" },
      { token: "{source_id}", description: "Affiliate source ID" },
    ],
  },
  {
    label: "Offer",
    macros: [
      { token: "{offer_id}", description: "Offer ID" },
      { token: "{offer_name}", description: "Offer name" },
      { token: "{offer_currency}", description: "Offer currency (USD, EUR, etc.)" },
      { token: "{offer_url_id}", description: "Offer URL ID" },
      { token: "{offer_url_name}", description: "Offer URL name" },
      { token: "{category}", description: "Offer category name" },
      { token: "{creative_id}", description: "Creative ID" },
    ],
  },
  {
    label: "Tracking / Time",
    macros: [
      { token: "{transaction_id}", description: "Unique transaction ID" },
      { token: "{datetime}", description: "UTC datetime (YYYY-MM-DD HH:MM:SS)" },
      { token: "{timestamp}", description: "Unix timestamp (UTC)" },
      { token: "{revenue_amount}", description: "Revenue amount" },
      { token: "{unique_click}", description: "Is unique click (1 or 0)" },
      { token: "{random}", description: "Random 9-digit number" },
    ],
  },
  {
    label: "Device",
    macros: [
      { token: "{device_type}", description: "Device type (mobile, tablet, desktop)" },
      { token: "{device_platform}", description: "OS name" },
      { token: "{device_os_version}", description: "OS version" },
      { token: "{device_browser}", description: "Browser name" },
      { token: "{browser_version}", description: "Browser version" },
      { token: "{user_agent}", description: "Full user-agent string" },
      { token: "{device_language}", description: "Device language code" },
    ],
  },
  {
    label: "Geo",
    macros: [
      { token: "{geo_country_code}", description: "Country code (ISO 3166-1)" },
      { token: "{geo_country_name}", description: "Country name" },
      { token: "{geo_region_name}", description: "Region name" },
      { token: "{geo_region_code}", description: "Region code" },
      { token: "{geo_city_name}", description: "City name" },
      { token: "{geo_isp_name}", description: "ISP name" },
      { token: "{geo_dma}", description: "DMA / metro code" },
      { token: "{geo_proxy}", description: "Is proxy (1 or 0)" },
    ],
  },
  {
    label: "Other",
    macros: [
      { token: "{referer}", description: "HTTP referer" },
      { token: "{user_ip}", description: "User IP address" },
      { token: "{tracking_domain}", description: "Tracking domain hostname" },
      { token: "{sec_ch_ua}", description: "Client hint: UA" },
      { token: "{sec_ch_mobile}", description: "Client hint: Mobile" },
      { token: "{sec_ch_platform}", description: "Client hint: Platform" },
    ],
  },
];

interface MacroPickerProps {
  onInsert: (macro: string) => void;
}

export function MacroPicker({ onInsert }: MacroPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const lowerSearch = search.toLowerCase();
  const filteredGroups = MACRO_GROUPS.map((group) => ({
    ...group,
    macros: group.macros.filter(
      (m) =>
        m.token.toLowerCase().includes(lowerSearch) ||
        m.description.toLowerCase().includes(lowerSearch),
    ),
  })).filter((g) => g.macros.length > 0);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(""); }}
        className={cn(
          "inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors",
          open
            ? "bg-gray-900 text-white border-gray-900"
            : "text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-gray-900",
        )}
        title="Insert macro"
      >
        <Code2 className="w-3.5 h-3.5" />
        Macros
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-80 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search macros..."
              className="w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
              autoFocus
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {filteredGroups.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-4">No macros match your search</p>
            )}
            {filteredGroups.map((group) => (
              <div key={group.label} className="mb-1">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 pt-2 pb-1">
                  {group.label}
                </p>
                {group.macros.map((macro) => (
                  <button
                    key={macro.token}
                    type="button"
                    onClick={() => {
                      onInsert(macro.token);
                      setOpen(false);
                    }}
                    className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-left rounded-md hover:bg-gray-50 transition-colors group"
                  >
                    <code className="text-xs font-mono text-gray-800 bg-gray-100 px-1.5 py-0.5 rounded group-hover:bg-gray-200 transition-colors">
                      {macro.token}
                    </code>
                    <span className="text-[11px] text-gray-500 truncate">{macro.description}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
